import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { cajaService } from '@/services/caja.service';
import { X, ArrowDownCircle, ArrowUpCircle, Loader2, Save, Plus, Printer } from 'lucide-react';
import { useApiError } from '@/hooks/useApiError';
import { ApiErrorDisplay } from '@/components/ApiErrorDisplay';
import { dialogs } from '@/stores/dialog-store';
import { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { WithdrawalTicket } from '@/components/WithdrawalTicket';

interface WithdrawalModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function WithdrawalModal({ isOpen, onClose }: WithdrawalModalProps) {
  const { user } = useAuthStore();
  const [registerId, setRegisterId] = useState<string | null>(null);
  const [movements, setMovements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormView, setIsFormView] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const { error, errorType, handleApiError, handleAuthRedirect } = useApiError();
  const printRef = useRef<HTMLDivElement>(null);
  const [movementToPrint, setMovementToPrint] = useState<any>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    onAfterPrint: () => setMovementToPrint(null),
  });

  useEffect(() => {
    if (movementToPrint) {
      setTimeout(() => {
        handlePrint();
      }, 100);
    }
  }, [movementToPrint, handlePrint]);

  const loadMovements = useCallback(async () => {
    if (!isOpen || !user?.branch?.id) return;
    setLoading(true);
    try {
      // First, get the active register ID
      const status = await cajaService.getStatus(user.branch.id);
      if (status.status === 'OPEN') {
        setRegisterId(status.id);
        // Then load its movements
        const result = await cajaService.getMovements(status.id);
        setMovements(result.filter((m: any) => m.type === 'OUT'));
      } else {
        setRegisterId(null);
      }
    } catch (err: any) {
      handleApiError(err);
    }
    setLoading(false);
  }, [isOpen, user]);

  useEffect(() => {
    loadMovements();
  }, [loadMovements]);

  // Reset view when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setIsFormView(false);
      setAmount('');
      setReason('');
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!registerId || !user?.id) return;
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) return;

    setSaving(true);
    try {
      const savedMovement = await cajaService.addMovement(registerId, {
        type: 'OUT',
        amount: numAmount,
        reason: reason || 'Retiro de efectivo',
        userId: user.id,
      });
      dialogs.alert('Retiro registrado correctamente', 'Éxito');
      setAmount('');
      setReason('');
      setIsFormView(false);
      loadMovements();
      setMovementToPrint(savedMovement);
      loadMovements();
    } catch (err: any) {
      dialogs.alert(err?.response?.data?.message || 'Error al registrar el retiro', 'Error');
    }
    setSaving(false);
  };

  const formatCurrency = (val: any) => `$${Number(val || 0).toFixed(2)}`;
  const formatTime = (d: string) => new Date(d).toLocaleTimeString('es-MX', {
    hour: '2-digit', minute: '2-digit'
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex bg-black/50 p-4 sm:p-6 lg:p-8 items-center justify-center">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden relative">
        <ApiErrorDisplay error={error} errorType={errorType} onRetry={loadMovements} onLogin={handleAuthRedirect} />
        {!error && (
          <>
            {/* Header */}
            <div className="p-6 border-b border-gray-100 flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-50 text-orange-500 rounded-2xl flex items-center justify-center">
                  <ArrowDownCircle size={24} strokeWidth={2} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {isFormView ? 'Retiro de Caja' : 'Retiros del Día'}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {isFormView ? 'Registra una salida de efectivo' : 'Historial de movimientos en esta sesión'}
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 min-h-[300px] flex flex-col">
              {isFormView ? (
                // Form View
                <div className="space-y-6 flex-1">
                  <div>
                    <label className="text-xs font-bold tracking-wider text-gray-400 uppercase mb-2 block">
                      Monto del Retiro
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-xl">$</span>
                      <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full bg-white border border-gray-200 rounded-xl py-3 pl-10 pr-4 text-xl font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                        step="0.01"
                        autoFocus
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold tracking-wider text-gray-400 uppercase mb-2 block">
                      Motivo / Concepto
                    </label>
                    <input
                      type="text"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Ej: Pago a proveedor, cambio, etc."
                      className="w-full bg-gray-50/50 border border-gray-200 rounded-xl py-3 px-4 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all focus:bg-white"
                    />
                  </div>
                </div>
              ) : (
                // List View
                <div className="flex-1 flex flex-col">
                  {loading ? (
                    <div className="flex-1 flex items-center justify-center">
                      <Loader2 size={24} className="animate-spin text-gray-400" />
                    </div>
                  ) : !registerId ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center">
                      <div className="w-16 h-16 rounded-full bg-gray-50 border-2 border-gray-100 flex items-center justify-center mb-4 text-gray-300">
                        <X size={32} />
                      </div>
                      <p className="font-bold text-gray-400 mb-4">No hay caja abierta</p>
                      <p className="text-sm text-gray-400">Abre la caja primero para hacer retiros.</p>
                    </div>
                  ) : movements.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center">
                      <div className="w-16 h-16 rounded-full bg-gray-50 border-2 border-gray-100 flex items-center justify-center mb-4 text-gray-300">
                        <ArrowDownCircle size={32} />
                      </div>
                      <p className="font-bold text-gray-400 mb-4">No hay retiros registrados</p>
                      <button
                        onClick={() => setIsFormView(true)}
                        className="px-6 py-2 bg-gray-50 hover:bg-gray-100 text-gray-500 text-xs font-bold tracking-wider uppercase rounded-xl transition-colors border border-gray-100 flex items-center gap-2"
                      >
                        <Plus size={14} /> Nuevo Retiro
                      </button>
                    </div>
                  ) : (
                    <div className="flex-1 overflow-y-auto space-y-3 -mx-2 px-2">
                      {movements.map((m: any) => (
                        <div key={m.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100/50">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center">
                              <ArrowDownCircle size={20} />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-gray-700">{m.reason || 'Retiro de efectivo'}</p>
                              <p className="text-xs text-gray-500">
                                {formatTime(m.createdAt)} • {m.user?.fullName}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <p className="font-bold text-gray-900">{formatCurrency(m.amount)}</p>
                            <button
                              onClick={() => setMovementToPrint(m)}
                              className="p-2 text-gray-400 hover:text-[#007AFF] hover:bg-blue-50 rounded-lg transition-colors"
                              title="Reimprimir Ticket"
                            >
                              <Printer size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-100 bg-gray-50/50">
              {isFormView ? (
                <div className="flex items-center justify-between gap-4">
                  <button
                    onClick={() => setIsFormView(false)}
                    className="flex-1 px-4 py-3 font-bold text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || !amount}
                    className="flex-1 px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-orange-500/20"
                  >
                    {saving ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
                    {saving ? 'Guardando...' : 'Registrar Retiro'}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setIsFormView(true)}
                  disabled={!registerId}
                  className="w-full px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-orange-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus size={20} />
                  Nuevo Retiro
                </button>
              )}
            </div>
          </>
        )}
      </div>
      <WithdrawalTicket ref={printRef} movement={movementToPrint} />
    </div>
  );
}
